/**
 * Response Formatter for Farm Manager AI
 *
 * Provides utilities for formatting AI responses in a consistent,
 * structured way. Ensures responses are actionable and well-organized.
 */

/**
 * Priority levels for recommendations with visual indicators
 */
const PRIORITY_INDICATORS = {
        critical: { emoji: "🔴", label: { en: "Critical", de: "Kritisch" } },
        high: { emoji: "🟠", label: { en: "High", de: "Hoch" } },
        medium: { emoji: "🟡", label: { en: "Medium", de: "Mittel" } },
        low: { emoji: "🟢", label: { en: "Low", de: "Niedrig" } },
        info: { emoji: "ℹ️", label: { en: "Info", de: "Info" } },
};

/**
 * Benchmark data for common KPIs
 */
const KPI_BENCHMARKS = {
        healthScore: {
                excellent: { min: 80, label: { en: "Excellent", de: "Ausgezeichnet" } },
                good: { min: 60, label: { en: "Good", de: "Gut" } },
                attention: { min: 40, label: { en: "Needs Attention", de: "Aufmerksamkeit nötig" } },
                critical: { min: 0, label: { en: "Critical", de: "Kritisch" } },
        },
        mortalityRate: {
                target: 5,
                unit: "%",
                interpretation: {
                        above: { en: "Above target - investigate causes", de: "Über Ziel - Ursachen untersuchen" },
                        below: { en: "Within target", de: "Im Zielbereich" },
                },
        },
        brdRate: {
                target: 10,
                unit: "%",
                interpretation: {
                        above: { en: "Above target - review health protocols", de: "Über Ziel - Gesundheitsprotokolle prüfen" },
                        below: { en: "Within target", de: "Im Zielbereich" },
                },
        },
        avgDailyGain: {
                excellent: 1.2,
                good: 0.9,
                average: 0.7,
                unit: "kg/day",
        },
        vaccinationCompliance: {
                target: 95,
                unit: "%",
                interpretation: {
                        above: { en: "Above target", de: "Über Ziel" },
                        below: { en: "Below target - schedule catch-up vaccinations", de: "Unter Ziel - Nachholimpfungen planen" },
                },
        },
};

/**
 * Formats a recommendation with priority indicator
 * @param {string} text - Recommendation text
 * @param {Object} options - Formatting options
 * @param {string} options.priority - Priority level (critical, high, medium, low, info)
 * @param {string} options.timeline - Optional timeline/urgency
 * @param {string} options.animalId - Optional animal ID to reference
 * @param {string} options.language - Language code (en, de)
 * @returns {string} Formatted recommendation string
 */
export function formatRecommendation(text, options = {}) {
        const { priority = "medium", timeline = null, animalId = null } = options;

        const indicator = PRIORITY_INDICATORS[priority] || PRIORITY_INDICATORS.info;
        let formatted = `${indicator.emoji} ${text}`;

        if (animalId) {
                formatted += ` [${animalId}]`;
        }

        if (timeline) {
                formatted += ` (${timeline})`;
        }

        return formatted;
}

/**
 * Formats a list of recommendations with priority ordering
 * @param {Array} recommendations - Array of { text, priority, timeline, animalId }
 * @param {Object} options - Formatting options
 * @param {string} options.language - Language code
 * @param {boolean} options.numbered - Whether to add numbers
 * @returns {string} Formatted recommendations list
 */
export function formatRecommendationsList(recommendations, options = {}) {
        const { language = "en", numbered = true } = options;

        if (!recommendations || recommendations.length === 0) {
                return "";
        }

        // Sort by priority
        const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
        const sorted = [...recommendations].sort(
                (a, b) => (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3)
        );

        return sorted
                .map((rec, index) => {
                        const formatted = formatRecommendation(rec.text, { ...rec, language });
                        return numbered ? `${index + 1}. ${formatted}` : `- ${formatted}`;
                })
                .join("\n");
}

/**
 * Interprets a health score value with context
 * @param {number} value - Health score (0-100)
 * @param {string} language - Language code
 * @returns {Object} Interpretation with level, label, and recommendation
 */
export function interpretHealthScore(value, language = "en") {
        if (typeof value !== "number" || Number.isNaN(value)) {
                return {
                        level: "unknown",
                        label: language === "de" ? "Unbekannt" : "Unknown",
                        recommendation: language === "de" ? "Daten nicht verfügbar" : "Data not available",
                };
        }

        const benchmarks = KPI_BENCHMARKS.healthScore;
        const isGerman = language === "de";

        if (value >= benchmarks.excellent.min) {
                return {
                        level: "excellent",
                        label: benchmarks.excellent.label[language] || benchmarks.excellent.label.en,
                        recommendation: isGerman
                                ? "Gesundheitswert im optimalen Bereich"
                                : "Health score in optimal range",
                };
        }

        if (value >= benchmarks.good.min) {
                return {
                        level: "good",
                        label: benchmarks.good.label[language] || benchmarks.good.label.en,
                        recommendation: isGerman
                                ? "Gesundheitswert akzeptabel, weiter beobachten"
                                : "Health score acceptable, continue monitoring",
                };
        }

        if (value >= benchmarks.attention.min) {
                return {
                        level: "attention",
                        label: benchmarks.attention.label[language] || benchmarks.attention.label.en,
                        recommendation: isGerman
                                ? "Tier zeigt Auffälligkeiten - Untersuchung empfohlen"
                                : "Animal showing anomalies - investigation recommended",
                };
        }

        return {
                level: "critical",
                label: benchmarks.critical.label[language] || benchmarks.critical.label.en,
                recommendation: isGerman
                        ? "Kritischer Zustand - sofortige Aufmerksamkeit erforderlich"
                        : "Critical condition - immediate attention required",
        };
}

/**
 * Formats a KPI value with benchmark comparison
 * @param {string} kpiKey - KPI identifier
 * @param {number} value - Current value
 * @param {Object} options - Formatting options
 * @param {string} options.language - Language code
 * @param {number} options.previousValue - Optional previous value for trend
 * @returns {Object} Formatted KPI with value, interpretation, and trend
 */
export function formatKPIValue(kpiKey, value, options = {}) {
        const { language = "en", previousValue = null } = options;
        const isGerman = language === "de";

        if (value === null || value === undefined || Number.isNaN(value)) {
                return {
                        formatted: isGerman ? "Nicht verfügbar" : "Not available",
                        interpretation: null,
                        trend: null,
                };
        }

        const benchmark = KPI_BENCHMARKS[kpiKey];
        const result = {
                value,
                formatted: `${value.toFixed(1)}${benchmark?.unit || ""}`,
                interpretation: null,
                trend: null,
        };

        // Add benchmark interpretation if available
        if (benchmark?.target !== undefined) {
                const isAboveTarget = value > benchmark.target;
                const interpretKey = isAboveTarget ? "above" : "below";
                result.interpretation = benchmark.interpretation?.[interpretKey]?.[language] ||
                        benchmark.interpretation?.[interpretKey]?.en ||
                        null;
        }

        // Calculate trend if previous value provided
        if (previousValue !== null && typeof previousValue === "number") {
                const change = value - previousValue;
                const percentChange = previousValue !== 0 ? (change / previousValue) * 100 : 0;

                result.trend = {
                        change,
                        percentChange,
                        direction: change > 0 ? "up" : change < 0 ? "down" : "stable",
                        formatted: `${change >= 0 ? "+" : ""}${change.toFixed(1)} (${percentChange >= 0 ? "+" : ""}${percentChange.toFixed(1)}%)`,
                };
        }

        return result;
}

/**
 * Formats a structured health assessment response
 * @param {Object} data - Assessment data
 * @param {Object} t - Translation function
 * @param {string} locale - Locale code
 * @returns {string} Formatted health assessment
 */
export function formatHealthAssessment(data, t, locale = "en") {
        const sections = [];
        const isGerman = locale.startsWith("de") || locale === "ger";

        // Summary section
        if (data.summary) {
                sections.push(`**${isGerman ? "Zusammenfassung" : "Summary"}:** ${data.summary}`);
        }

        // Findings with severity indicators
        if (data.findings?.length > 0) {
                sections.push(`\n**${isGerman ? "Befunde" : "Key Findings"}:**`);
                data.findings.forEach((finding) => {
                        sections.push(
                                formatRecommendation(finding.text, {
                                        priority: finding.severity || "info",
                                        animalId: finding.animalId,
                                        language: locale,
                                })
                        );
                });
        }

        // Interpretation
        if (data.interpretation) {
                sections.push(`\n**${isGerman ? "Interpretation" : "Interpretation"}:** ${data.interpretation}`);
        }

        // Prioritized recommendations
        if (data.recommendations?.length > 0) {
                sections.push(`\n**${isGerman ? "Empfehlungen" : "Recommendations"}:**`);
                sections.push(
                        formatRecommendationsList(data.recommendations, { language: locale, numbered: true })
                );
        }

        // Follow-up actions
        if (data.followUp?.length > 0) {
                sections.push(`\n**${isGerman ? "Nächste Schritte" : "Next Steps"}:**`);
                data.followUp.forEach((action) => {
                        sections.push(`- ${action}`);
                });
        }

        return sections.join("\n");
}

/**
 * Formats a data interpretation with context
 * @param {number} value - The value to interpret
 * @param {Object} context - Interpretation context
 * @param {string} context.type - Type of value (healthScore, mortalityRate, avgDailyGain, etc.)
 * @param {Object} context.benchmark - Benchmark data { value, label, tolerance }
 * @param {number} context.trend - Trend percentage
 * @param {string} context.unit - Unit of measurement
 * @param {Function} t - Translation function
 * @returns {string} Formatted interpretation
 */
export function formatDataInterpretation(value, context, t) {
        const { benchmark, trend, unit } = context;
        const parts = [];

        // Value with unit
        const formattedValue = typeof value === "number" ? value.toFixed(1) : value;
        parts.push(`${formattedValue}${unit || ""}`);

        // Compare to benchmark
        if (benchmark?.value !== undefined) {
                const diff = value - benchmark.value;
                const percentDiff = benchmark.value !== 0 ? ((diff / benchmark.value) * 100).toFixed(1) : 0;
                const tolerance = benchmark.tolerance || 5;

                if (Math.abs(diff) < tolerance) {
                        parts.push(t("within-normal-range", { defaultValue: "within normal range" }));
                } else if (diff > 0) {
                        parts.push(
                                t("above-benchmark", {
                                        percent: percentDiff,
                                        benchmark: benchmark.label,
                                        defaultValue: `${percentDiff}% above ${benchmark.label}`,
                                })
                        );
                } else {
                        parts.push(
                                t("below-benchmark", {
                                        percent: Math.abs(percentDiff),
                                        benchmark: benchmark.label,
                                        defaultValue: `${Math.abs(percentDiff)}% below ${benchmark.label}`,
                                })
                        );
                }
        }

        // Add trend context
        if (trend !== undefined && trend !== null) {
                const trendText =
                        trend > 0
                                ? t("trending-up", { percent: trend.toFixed(1), defaultValue: `trending up ${trend.toFixed(1)}%` })
                                : t("trending-down", { percent: Math.abs(trend).toFixed(1), defaultValue: `trending down ${Math.abs(trend).toFixed(1)}%` });
                parts.push(trendText);
        }

        return parts.join(" - ");
}

/**
 * Validates response quality and suggests improvements
 * @param {string} response - AI response text
 * @param {Object} context - Response context
 * @param {boolean} context.isDataQuery - Whether this was a data query
 * @param {boolean} context.isRecommendation - Whether recommendations were expected
 * @param {Array} context.animalIds - Animal IDs that should be referenced
 * @returns {Object} Validation result with issues and suggestions
 */
export function validateResponseQuality(response, context = {}) {
        const issues = [];
        const suggestions = [];

        if (!response || typeof response !== "string") {
                return { valid: false, issues: ["Empty or invalid response"], suggestions: [] };
        }

        // Check for generic response patterns
        const genericPatterns = [
                /I don't have enough information/i,
                /I cannot access/i,
                /please provide more details/i,
                /I'm not sure/i,
                /unfortunately/i,
        ];

        if (genericPatterns.some((p) => p.test(response))) {
                issues.push("generic-response");
                suggestions.push("Response seems generic - consider using tools to gather specific data");
        }

        // Check for missing data references in data queries
        if (context.isDataQuery && context.animalIds?.length > 0) {
                const mentionedIds = context.animalIds.filter((id) => response.includes(id));
                if (mentionedIds.length === 0) {
                        issues.push("missing-references");
                        suggestions.push("Consider referencing specific animal IDs in the response");
                }
        }

        // Check for missing action items in recommendations
        if (context.isRecommendation) {
                const hasActionItems =
                        /\d+\.\s|•\s|-\s/.test(response) || // List indicators
                        /should|recommend|suggest|consider|need to|must/i.test(response); // Action language

                if (!hasActionItems) {
                        issues.push("missing-actions");
                        suggestions.push("Include specific actionable steps in recommendations");
                }
        }

        // Check response length
        if (response.length < 50) {
                issues.push("too-short");
                suggestions.push("Response may be too brief - consider adding more context");
        }

        return {
                valid: issues.length === 0,
                issues,
                suggestions,
        };
}

const responseFormatter = {
        formatRecommendation,
        formatRecommendationsList,
        interpretHealthScore,
        formatKPIValue,
        formatHealthAssessment,
        formatDataInterpretation,
        validateResponseQuality,
        PRIORITY_INDICATORS,
        KPI_BENCHMARKS,
};

export default responseFormatter;
